import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ShoppingCart,
  Trash2,
  Minus,
  Plus,
  ChevronLeft,
  Check,
  PackageOpen,
  Loader2,
} from 'lucide-react';
import { cartApi, cartStorage, shopApi } from '@/user/api';
import { tokenStorage } from '@/user/api';
import type { CartItem } from '@/types/api-entity';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface CartDisplayItem {
  productId: number;
  productName: string;
  mainImage?: string;
  price: number;
  quantity: number;
  unit?: string;
  selected: boolean;
}

export default function Cart() {
  const [cartItems, setCartItems] = useState<CartDisplayItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const isLoggedIn = tokenStorage.isLoggedIn();

  const loadCart = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (isLoggedIn) {
        // 已登录：从后端获取购物车，再获取商品详情
        const cartRes = await cartApi.getCartList();
        if (cartRes.code === 200 && cartRes.data) {
          const backendItems = cartRes.data;
          // 并行获取商品详情
          const productPromises = backendItems.map((item) =>
            shopApi.getProductDetail(item.productId).catch(() => null)
          );
          const productResults = await Promise.all(productPromises);
          const items: CartDisplayItem[] = backendItems.map((cartItem, idx) => {
            const product = productResults[idx]?.data;
            return {
              productId: cartItem.productId,
              productName: product?.productName || `商品#${cartItem.productId}`,
              mainImage: product?.mainImage,
              price: product?.price || 0,
              quantity: cartItem.quantity,
              unit: product?.unit,
              selected: true,
            };
          });
          setCartItems(items);
        }
      } else {
        // 未登录：使用本地购物车
        const localItems = cartStorage.getCart();
        setCartItems(
          localItems.map((item) => ({
            productId: item.productId,
            productName: item.productName,
            mainImage: item.mainImage,
            price: item.price,
            quantity: item.quantity,
            unit: item.unit,
            selected: item.selected ?? true,
          }))
        );
      }
    } catch (err) {
      setError('加载购物车失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    loadCart();
  }, [loadCart]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const handleSelectAll = async (checked: boolean) => {
    if (isLoggedIn) {
      setCartItems((prev) => prev.map((item) => ({ ...item, selected: checked })));
    } else {
      cartStorage.selectAll(checked);
      loadCart();
    }
  };

  const handleSelectItem = (productId: number, checked: boolean) => {
    if (isLoggedIn) {
      setCartItems((prev) =>
        prev.map((item) => (item.productId === productId ? { ...item, selected: checked } : item))
      );
    } else {
      cartStorage.updateSelected(productId, checked);
      loadCart();
    }
  };

  const handleQuantityChange = async (productId: number, delta: number) => {
    const item = cartItems.find((i) => i.productId === productId);
    if (!item) return;
    const newQty = Math.max(1, item.quantity + delta);

    if (isLoggedIn) {
      try {
        await cartApi.updateQuantity(productId, newQty);
        setCartItems((prev) =>
          prev.map((i) => (i.productId === productId ? { ...i, quantity: newQty } : i))
        );
      } catch {
        setError('更新数量失败');
      }
    } else {
      cartStorage.updateQuantity(productId, newQty);
      loadCart();
    }
  };

  const handleRemove = async (productId: number) => {
    if (isLoggedIn) {
      try {
        await cartApi.removeFromCart(productId);
        setCartItems((prev) => prev.filter((i) => i.productId !== productId));
      } catch {
        setError('删除失败');
      }
    } else {
      cartStorage.removeFromCart(productId);
      loadCart();
    }
  };

  const handleClear = async () => {
    if (!confirm('确定要清空购物车吗？')) return;
    if (isLoggedIn) {
      try {
        await cartApi.clearCart();
        setCartItems([]);
      } catch {
        setError('清空失败');
      }
    } else {
      cartStorage.clearCart();
      loadCart();
    }
  };

  const selectedItems = cartItems.filter((i) => i.selected);
  const selectedTotal = selectedItems.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const selectedCount = selectedItems.reduce((sum, i) => sum + i.quantity, 0);
  const selectAll = cartItems.length > 0 && cartItems.every((i) => i.selected);
  const getFirstImage = (mainImage: string | string[] | undefined | null): string => {
    if (!mainImage) return 'https://via.placeholder.com/100';
    
    let firstUrl = '';
    if (Array.isArray(mainImage)) {
      firstUrl = mainImage[0] || '';
    } else if (typeof mainImage === 'string') {
      const urls = mainImage.split(',');
      firstUrl = urls.length > 0 ? urls[0].trim() : '';
    }
    
    return firstUrl || 'https://via.placeholder.com/100';
  };
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-1 pt-24 pb-8">
        <div className="section-container">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ShoppingCart className="w-6 h-6 text-[#1c5bff]" />
              购物车
              {cartItems.length > 0 && (
                <span className="text-sm font-normal text-gray-500">（{cartItems.length} 种商品）</span>
              )}
            </h1>
          </div>

          {!isLoggedIn && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-center justify-between">
              <span className="text-amber-700 text-sm">登录后可同步购物车到云端，换设备不丢失</span>
              <Link
                to="/login"
                className="px-4 py-1.5 bg-amber-500 text-white text-sm rounded-lg hover:bg-amber-600 transition-colors"
              >
                去登录
              </Link>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-red-600 text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="bg-white rounded-2xl shadow-sm p-16 text-center">
              <Loader2 className="w-8 h-8 text-[#1c5bff] animate-spin mx-auto mb-4" />
              <p className="text-gray-500">加载中...</p>
            </div>
          ) : cartItems.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm p-16 text-center">
              <PackageOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-2">购物车是空的</p>
              <p className="text-gray-400 text-sm mb-6">快去挑选心仪的商品吧</p>
              <Link
                to="/shop"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#1c5bff] text-white rounded-lg hover:bg-[#1547cc] transition-colors"
              >
                <ShoppingCart className="w-5 h-5" />
                去购物
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4">
                <div className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-[#1c5bff] focus:ring-[#1c5bff]"
                    />
                    <span className="text-sm text-gray-600">全选</span>
                  </label>
                  <button
                    onClick={handleClear}
                    className="text-sm text-red-500 hover:text-red-600 flex items-center gap-1 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    清空购物车
                  </button>
                </div>

                {cartItems.map((item) => (
                  <div
                    key={item.productId}
                    className={`bg-white rounded-xl shadow-sm p-4 flex gap-4 transition-all ${
                      item.selected ? 'ring-1 ring-[#1c5bff]/20' : ''
                    }`}
                  >
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={item.selected}
                        onChange={(e) => handleSelectItem(item.productId, e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-[#1c5bff] focus:ring-[#1c5bff]"
                      />
                    </div>

                    <Link
                      to={`/shop/product/${item.productId}`}
                      className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0"
                    >
                      <img
                        src={getFirstImage(item.mainImage) || 'https://via.placeholder.com/100'}
                        alt={item.productName}
                        className="w-full h-full object-cover"
                      />
                    </Link>

                    <div className="flex-1 flex flex-col justify-between">
                      <div>
                        <Link
                          to={`/shop/product/${item.productId}`}
                          className="font-medium text-gray-900 hover:text-[#1c5bff] transition-colors line-clamp-2"
                        >
                          {item.productName}
                        </Link>
                        <p className="text-sm text-gray-500 mt-1">
                          单价: {formatPrice(item.price)}
                        </p>
                      </div>

                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center border border-gray-200 rounded-lg">
                          <button
                            onClick={() => handleQuantityChange(item.productId, -1)}
                            className="p-2 hover:bg-gray-100 transition-colors"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 1;
                              if (isLoggedIn) {
                                cartApi.updateQuantity(item.productId, val).then(() => {
                                  setCartItems((prev) =>
                                    prev.map((i) =>
                                      i.productId === item.productId ? { ...i, quantity: val } : i
                                    )
                                  );
                                });
                              } else {
                                cartStorage.updateQuantity(item.productId, val);
                                loadCart();
                              }
                            }}
                            className="w-12 text-center border-x border-gray-200 py-2 outline-none text-sm"
                          />
                          <button
                            onClick={() => handleQuantityChange(item.productId, 1)}
                            className="p-2 hover:bg-gray-100 transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="flex items-center gap-4">
                          <span className="text-lg font-bold text-[#1c5bff]">
                            {formatPrice(item.price * item.quantity)}
                          </span>
                          <button
                            onClick={() => handleRemove(item.productId)}
                            className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                            title="删除"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl shadow-sm p-6 sticky top-24">
                  <h2 className="text-lg font-bold text-gray-900 mb-4">订单摘要</h2>

                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">商品种类</span>
                      <span className="text-gray-900">{cartItems.length}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">选中数量</span>
                      <span className="text-gray-900">{selectedCount} {cartItems[0]?.unit || '件'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">运费</span>
                      <span className="text-green-600">免运费</span>
                    </div>
                  </div>

                  <div className="border-t border-gray-100 pt-4 mb-6">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">合计</span>
                      <span className="text-2xl font-bold text-[#1c5bff]">
                        {formatPrice(selectedTotal)}
                      </span>
                    </div>
                  </div>

                  <button
                    disabled={selectedCount === 0}
                    onClick={() => navigate('/checkout')}
                    className="w-full py-3 bg-[#1c5bff] text-white font-semibold rounded-lg hover:bg-[#1547cc] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <Check className="w-5 h-5" />
                    去结算
                  </button>

                  <Link
                    to="/shop"
                    className="mt-3 w-full py-3 border border-gray-200 text-gray-600 font-medium rounded-lg hover:bg-gray-50 transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    继续购物
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
